import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import { LegendOrdinal } from "@visx/legend";
import { scaleOrdinal } from "@visx/scale";
import { Group } from "@visx/group";
import { XYChart, AnimatedAxis, AnimatedLineSeries, Tooltip } from "@visx/xychart";
import { RectClipPath } from "@visx/clip-path";
import {
  displayNames,
  oldMFDseries,
  seriesNameToColour,
  subductionSeries,
  v1MFDseries,
} from "../../constants/mfdSeries";
import { graphql } from "react-relay";

export interface IMagRate {
  mag: number;
  rate: number;
}

export interface Series {
  data: IMagRate[];
  name: string;
  displayName: string;
  color: string;
}

export interface MfdTableMeta {
  id: string;
  isV2: boolean;
  subduction: boolean;
  source: string; // for debugging
}

export interface SeriesNames {
  Both: string[];
  TVZ: string[];
  SansTVZ: string[];
  preV2: string[];
}

interface QueryResult {
  node?:
    | {
        tables?:
          | readonly (
              | {
                  readonly table_id: string | null | undefined;
                  readonly table_type: string | null | undefined;
                }
              | undefined
              | null
            )[]
          | undefined
          | null;
        readonly mfd_table_id?: string | null | undefined;
        readonly mfd_table?:
          | {
              readonly id: string;
            }
          | null
          | undefined;
        meta?:
          | readonly (
              | {
                  readonly k: string | null | undefined;
                  readonly v: string | null | undefined;
                }
              | null
              | undefined
            )[]
          | null
          | undefined;
      }
    | undefined
    | null;
}

/**
 * Extracts Table meta data from an InversionSolution object.
 * @param data
 * @returns
 */
export const useMfdTableMeta = (data?: QueryResult | undefined | null): MfdTableMeta | undefined => {
  return useMemo(() => {
    if (!data?.node) {
      return undefined;
    }
    const subduction = data.node.meta?.filter((kv) => kv?.k == "config_type")[0]?.v == "subduction";
    const tables = data.node.tables;
    const V2table = tables?.find((table) => table?.table_type === "MFD_CURVES_V2");
    const V1table = tables?.find((table) => table?.table_type === "MFD_CURVES");
    if (V2table?.table_id) {
      return { id: V2table.table_id, isV2: true, subduction, source: "tables" };
    }
    if (V1table?.table_id) {
      return { id: V1table.table_id, isV2: false, subduction, source: "tables" };
    }
    if (data.node.mfd_table?.id) {
      return { id: data.node.mfd_table.id, isV2: true, subduction, source: "mfd_table" };
    }
    if (data.node.mfd_table_id) {
      return { id: data.node.mfd_table_id, isV2: false, subduction, source: "mfd_table_id" };
    }
    // No table for this solution. Callers rely on `undefined` meaning exactly
    // that, so it must not hold on to the previous solution's value.
    return undefined;
  }, [data]);
};

/**
 * Returns the names of all series that are available in the raw GraphQL Table rows.
 * These series names can be used in `parseSeries()` in order to create data for an `MfdChart`.
 * @param rows the GraphQL Table rows
 * @param isSubduction whether the table is for a subduction inversion
 * @param isV2 whether the table is V2
 * @returns a SeriesNames object. Series names for pre-V2 tables are found in `preV2`
 */
export function getSeriesNames(
  rows: readonly (readonly (string | null | undefined)[] | null | undefined)[] | null | undefined,
  isSubduction: boolean,
  isV2: boolean,
): SeriesNames {
  const both: string[] = [];
  const tvz: string[] = [];
  const sans: string[] = [];
  const preV2: string[] = [];
  let last: string;
  let mask: string[];

  if (!isV2) {
    const isOldTable = rows?.some((value) => {
      return value?.includes("InversionTargetMFDs.targetOnFaultSupraSeisMFD_SansTVZ");
    });
    if (isSubduction) {
      mask = subductionSeries;
    } else if (isOldTable) {
      mask = oldMFDseries;
    } else {
      mask = v1MFDseries;
    }
  }

  rows?.forEach((row) => {
    if (!row?.[1]) {
      return;
    }
    const name = row[1];

    if (last === name) {
      return;
    }

    last = name;

    if (!isV2) {
      if (mask.includes(name)) {
        preV2.push(name);
      }
      return;
    }

    if (name.includes("SansTVZ")) {
      sans.push(name);
      return;
    }
    if (name.includes("TVZ")) {
      tvz.push(name);
      return;
    }
    both.push(name);
  });

  return { Both: both, TVZ: tvz, SansTVZ: sans, preV2 };
}

const MAGS = Array.from({ length: 50 }, (_v, i) => {
  return Math.round((5.05 + i / 10) * 100) / 100;
});

const MIN_ZERO = 1e-20; //log scales cannot include 0

/**
 * Extracts and parses one series from the raw GraphQL Table rows.
 * @param rows the GraphQL Table rows
 * @param seriesName the name of thew series
 * @returns The parsed Series object.
 */
export function parseSeries(
  rows: readonly (readonly (string | null | undefined)[] | null | undefined)[] | null | undefined,
  seriesName: string,
): Series {
  let data: Array<IMagRate> = [];
  if (rows) {
    // get the correct series
    const rawSeries = rows.filter((row) => row && row[1] == seriesName) as string[][];
    const parsed = rawSeries.map((row) => [parseFloat(row[2] ?? ""), parseFloat(row[3] ?? "")]);
    data = MAGS.map((mag) => {
      for (const [m, r] of parsed) {
        if (m == mag) {
          return { mag, rate: Math.max(r, MIN_ZERO) };
        }
      }
      return { mag, rate: MIN_ZERO };
    });
  }
  return {
    data,
    name: seriesName,
    color: seriesNameToColour(seriesName),
    displayName: displayNames[seriesName as keyof typeof displayNames] || seriesName,
  };
}

export interface MfdChartProps {
  series: Series[];
  mfdTableMeta: MfdTableMeta;
  width: number;
  title?: string;
}

/**
 * An MFD chart, drawn using SVG.
 *
 * - Use `useMfdTableMeta()` to get Table meta data from InversioSolution.
 * - Use `useLazyLoadQuery<MfdChartQuery>(mfdChartQuery, { id })` to load Table data.
 * - Use `getSeriesNames()` to get available series in Table data.
 * - Use `parseSeries()` to parse the series you want to render.
 *
 * @param series An array of `Series` objects from `parseSeries()`.
 * @param mfdTableMeta An `MfdTableMeta` object from `useMfdTableMeta()`
 * @param width The width of the chart in pixels.
 * @param title an optional chart title.
 * @returns a React component representing an MFD chart
 */
const MfdChart: React.FC<MfdChartProps> = ({ series, mfdTableMeta, width, title }: MfdChartProps) => {
  if (!mfdTableMeta.id || !series?.length) {
    return <>No data available.</>;
  }

  const scale = scaleOrdinal({
    domain: series.map((series) => series.displayName),
    range: series.map((series) => series.color),
  });

  const [minMag, maxMag] = mfdTableMeta?.subduction ? [6.5, 9.5] : [5, 9];

  const height = width * 0.75;
  const margin = { bottom: 30, left: 50, right: 50, top: 10 };
  const xMax = width;
  const yMax = height - margin.bottom;

  console.log(
    `MFD ${mfdTableMeta.id} ${mfdTableMeta.subduction ? "subduction" : "crustal"} ${mfdTableMeta.source} ${mfdTableMeta.isV2 ? "V2" : ""}`,
  );

  for (const s of series) {
    s.data = s.data.filter((p) => p.mag >= minMag && p.mag <= maxMag);
  }

  return (
    <>
      {title && (
        <Typography
          style={{ width: "100%", textAlign: "center", fontWeight: "bold", fontSize: Math.min(width * 0.035, 18) }}
        >
          {title}
        </Typography>
      )}
      <div style={{ position: "relative" }}>
        <XYChart
          height={height}
          width={width}
          margin={margin}
          xScale={{ type: "linear", domain: [minMag, maxMag], zero: false }}
          yScale={{ type: "log", domain: [1e-6, 1] }}
        >
          <AnimatedAxis
            orientation="bottom"
            tickLabelProps={() => ({
              fontSize: Math.min(10, width * 0.03),
            })}
            numTicks={width < 400 ? 5 : undefined}
          />
          <AnimatedAxis
            orientation="left"
            tickFormat={(value) => {
              if (value === 1) {
                return "1";
              }
              const scientific = value.toExponential();
              if (scientific.startsWith("1e")) {
                return scientific;
              }
              return "";
            }}
            tickLabelProps={() => ({
              fontSize: Math.min(10, width * 0.03),
            })}
          />
          <RectClipPath id={"mfd-clip" + xMax} height={yMax} width={xMax} />
          <Group clipPath={"url(#mfd-clip" + xMax}>
            {series.map((curve) => {
              return (
                <AnimatedLineSeries
                  key={curve.name}
                  dataKey={curve.name}
                  xAccessor={(d: IMagRate) => d?.mag}
                  yAccessor={(d: IMagRate) => d?.rate}
                  stroke={curve.color}
                  data={curve.data}
                />
              );
            })}
          </Group>
          <Tooltip
            snapTooltipToDatumX
            snapTooltipToDatumY
            showDatumGlyph
            glyphStyle={{ fill: "#000" }}
            renderTooltip={({ tooltipData }) => {
              const datum = tooltipData?.nearestDatum?.datum as IMagRate;
              const key = tooltipData?.nearestDatum?.key;
              return (
                <>
                  <strong>{displayNames[key as keyof typeof displayNames] || key}</strong>
                  <Typography>mag: {datum.mag}</Typography>
                  <Typography>rate: {datum.rate}</Typography>
                </>
              );
            }}
          />
        </XYChart>

        <LegendOrdinal
          direction="column"
          scale={scale}
          shape="line"
          style={{
            float: "right",
            fontSize: width * 0.02,
            position: "absolute",
            top: 0,
            right: 0,
          }}
          shapeHeight={height * 0.03}
        />
      </div>
    </>
  );
};

export default MfdChart;

export const mfdChartQuery = graphql`
  query MfdChartQuery($id: ID!) {
    node(id: $id) {
      ... on Table {
        id
        name
        column_types
        column_headers
        rows
      }
    }
  }
`;
